import {
  CustomerApplicationStatusEnum,
  Mutation,
  Query,
  SubmitAdvanceAccountApplicationInput,
  Typenames,
} from '@varo/graphql-schema-types';
import {
  MAX_RETRIES,
  REFETCH_QUERY_INTERVAL,
} from 'features/advance/constants/constants';
import { useEffect, useState } from 'react';
import { SubmitAdvanceAccountApplication } from 'services/graphql/mutations/Advance/SubmitAdvanceAccountApplication';
import { ApplicationStatus as ApplicationStatusQuery } from 'services/graphql/queries/advance/ApplicationStatus';
import { useMutation, useQuery } from 'urql';

const useSubmitEmploymentInfo = () => {
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [retries, setRetries] = useState(0);
  const [advanceStatus, setAdvanceStatus] =
    useState<CustomerApplicationStatusEnum | null>(null);

  const [
    { data: response, fetching: fetchingApplication, error: applicationError },
    submitAdvanceAccountApplication,
  ] = useMutation<Mutation>(SubmitAdvanceAccountApplication);

  const mutationError =
    response?.submitAdvanceAccountApplication?.__typename ===
    Typenames.SubmitAdvanceAccountApplicationError
      ? response.submitAdvanceAccountApplication
      : null;

  const isMissingAgreements =
    response?.submitAdvanceAccountApplication?.__typename ===
    Typenames.SubmitAdvanceAccountApplicationMissingAgreement;

  // Effect for the first API call (submitAdvanceAccountApplication)
  useEffect(() => {
    if (
      response?.submitAdvanceAccountApplication?.__typename ===
      Typenames.SubmitAdvanceAccountApplicationSuccess
    ) {
      const appSuccess = response.submitAdvanceAccountApplication;
      setApplicationId(appSuccess.applicationId as string);
    }
  }, [response]);

  // Second API call only triggers if applicationId is set
  const [{ data: applicationData, fetching, error: queryError }, refetch] =
    useQuery<Query>({
      query: ApplicationStatusQuery,
      requestPolicy: 'network-only',
      pause: !applicationId, // Triggers query only if applicationId is set
      variables: { applicationStatusId: applicationId },
    });

  // Handle retries and fetching application status logic
  useEffect(() => {
    if (
      applicationData?.applicationStatus ===
        CustomerApplicationStatusEnum.Pending &&
      retries < MAX_RETRIES
    ) {
      const retryTimeout = setTimeout(() => {
        setRetries((prev) => prev + 1);
        refetch();
      }, REFETCH_QUERY_INTERVAL);

      return () => clearTimeout(retryTimeout); // Cleanup timeout on re-run or unmount
    } else if (applicationData?.applicationStatus) {
      setAdvanceStatus(applicationData.applicationStatus);
    }
  }, [applicationData, retries, refetch]);

  // Redirect or take action after retries complete and status is no longer pending
  useEffect(() => {
    if (advanceStatus) {
      setShowLoadingScreen(fetching);
    }
  }, [advanceStatus, retries, fetching]);

  const submitApplication = (args: {
    input: SubmitAdvanceAccountApplicationInput;
    idempotencyId: string;
  }) => {
    setShowLoadingScreen(true);
    return submitAdvanceAccountApplication(args);
  };

  return {
    isMissingAgreements,
    advanceStatus,
    fetching: fetchingApplication || showLoadingScreen || fetching,
    employmentInfoError: mutationError || queryError || applicationError,
    response,
    submitAdvanceAccountApplication: submitApplication,
  };
};

export default useSubmitEmploymentInfo;
